import { QueryClientProvider } from '@tanstack/react-query';
import { Outlet, createRootRoute } from '@tanstack/react-router';
import * as React from 'react';

import '~/styles.css';
import { AppShell } from '~/components/app-shell';
import { SetupCheck } from '~/components/setup-check';
import { ActionBarProvider } from '~/lib/action-bar';
import { AuthProvider, useAuth } from '~/lib/auth';
import { PresentationModeProvider } from '~/lib/presentation-mode';
import { queryClient } from '~/lib/query-client';
import { ThemeProvider } from '~/lib/theme';

let Devtools: React.ComponentType = () => null;

if (import.meta.env.DEV) {
  const TanStackRouterDevtools = React.lazy(() =>
    import('@tanstack/react-router-devtools').then((module) => ({
      default: module.TanStackRouterDevtools
    }))
  );

  const ReactQueryDevtools = React.lazy(() =>
    import('@tanstack/react-query-devtools').then((module) => ({
      default: module.ReactQueryDevtools
    }))
  );

  Devtools = function Devtools() {
    return (
      <React.Suspense fallback={null}>
        <TanStackRouterDevtools />
        <ReactQueryDevtools initialIsOpen={false} />
      </React.Suspense>
    );
  };
}

export const Route = createRootRoute({
  component: RootComponent
});

function RootComponent() {
  return (
    <React.StrictMode>
      <div className="flex h-full min-h-0 flex-col">
        <QueryClientProvider client={queryClient}>
          <div className="flex min-h-0 flex-1 flex-col">
            <ThemeProvider defaultTheme="system" storageKey="dataroom-theme">
              <AuthProvider>
                <AuthenticatedPresentationMode>
                  <ActionBarProvider>
                    <SetupCheck>
                      <AppShell>
                        <Outlet />
                      </AppShell>
                    </SetupCheck>
                  </ActionBarProvider>
                </AuthenticatedPresentationMode>
              </AuthProvider>
            </ThemeProvider>
          </div>
          <Devtools />
        </QueryClientProvider>
      </div>
    </React.StrictMode>
  );
}

function AuthenticatedPresentationMode({
  children
}: {
  children: React.ReactNode;
}) {
  const { user } = useAuth();
  const storageKey = user
    ? `dataroom-presentation-mode:v1:${user.id}`
    : 'dataroom-presentation-mode:v1:anonymous';

  return (
    <PresentationModeProvider storageKey={storageKey}>
      {children}
    </PresentationModeProvider>
  );
}
